import { SyntheticEvent, useState } from "react"
import { Box, Paper, SxProps } from "@mui/material"
import { Button, Divider } from "@tm/components"
import { Tab } from "../Tabs/Tab"
import { Tabs } from "../Tabs/Tabs"
import { TabPanel } from "../Tabs/TabPanel"

type WidgetProps = {
    key: string
    label: string
    tabContent: () => JSX.Element
}

type MultiWidgetProps = {
    widgets: WidgetProps[]
    enableContentDivider?: boolean
    enablePaper?: boolean
    paperSx?: SxProps
    variant?: "tabs" | "buttons"
}

export function MultiWidget({ widgets, enableContentDivider, enablePaper, paperSx, variant = "tabs" }: MultiWidgetProps) {
    const [value, setValue] = useState(widgets[0].key)

    const handleChange = (_: SyntheticEvent | null, newValue: string) => {
        setValue(newValue)
    }

    const isSelected = (key: string) => value === key

    const renderTabControls = () => {
        if (variant === "tabs") {
            return (
                <Tabs variant="scrollable" value={value} onChange={handleChange}>
                    {widgets.map((widget) => (
                        <Tab
                            key={widget.key}
                            value={widget.key}
                            label={widget.label}
                            sx={{ "&.MuiTab-root": { marginRight: "-6px", padding: "11px 8px 4px" } }}
                        />
                    ))}
                </Tabs>
            )
        }

        // Variant: buttons
        return (
            <Box display="flex" gap={0.5} mb={2}>
                {widgets.map((widget) => {
                    return (
                        <Button
                            key={widget.key}
                            variant="contained"
                            color={isSelected(widget.key) ? "primary" : undefined}
                            onClick={() => handleChange(null, widget.key)}
                        >
                            {widget.label}
                        </Button>
                    )
                })}
            </Box>
        )
    }

    const renderTabsContent = () => (
        <>
            {enableContentDivider && <Divider sx={{ marginBottom: "16px", borderColor: "#666" }} />}

            {widgets.map((widget) => {
                const Component = widget.tabContent
                return (
                    <TabPanel key={widget.key} value={widget.key} index={value} sx={{ paddingLeft: variant !== "tabs" ? 0 : "8px" }}>
                        <Component />
                    </TabPanel>
                )
            })}
        </>
    )

    const content = (
        <Box padding="1.5em">
            {renderTabControls()}
            {renderTabsContent()}
        </Box>
    )

    return enablePaper ? <Paper sx={{ borderRadius: "3px", ...paperSx }}>{content}</Paper> : content
}
